import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Table, Column, AutoSizer } from 'react-virtualized';
import { Button, Col, Row, Spinner } from 'reactstrap';
import { Chart as ChartJS, registerables } from 'chart.js';
import Title from '../../../sharedComponents/Title/Title';
import useDataSuite from '../useDataSuite';
import { FaClipboardList, FaTrash } from 'react-icons/fa';
import { BsPencilSquare } from 'react-icons/bs';
import formatters from '../../../config/formatters';
import { IoMdInformationCircle } from "react-icons/io";
import { Tooltip } from "@mui/material";
import useDataSuiteWidgets from './useDataSuiteWidgets';
import { isEqual } from "lodash";
import EmptyDataMessage from '../../../sharedComponents/ErrorMessages/EmptyDataMessage';
import EmptySearchResultsMessage from '../../../sharedComponents/ErrorMessages/EmptySearchResultsMessage';
import { Theme } from '../../../config/constants';

ChartJS.register(...registerables);

const LostLocationsWidget = (props) => {
    const navigate = useNavigate();
    const { w, i } = props;
    const {  onClickWidgetDrawerOpen, onClickDeleteWidget } = useDataSuite.useWidgetControls();
    const { widgetTitle, lostLocationsTableData, sortedTableData, filtersEnabled } = useDataSuiteWidgets.useLostLocationsWidget();
    const widgetOptionsOpen = useSelector(({ui}) => ui.slideDrawer);
    const selectedFilters = useSelector(({datasuite}) => datasuite.selectedFilters);
    const selectedDetailsFilters = useSelector(({datasuite}) => datasuite.selectedDetailsFilters);
    const dateString = useSelector(({dates}) => dates.datasuiteDateString);
    const detailsDateString = useSelector(({dates}) => dates.datasuiteDetailsDateString);
    const isLoading = useSelector(({datasuite}) => datasuite.isLoading.lostLocationsLoading);
    const { getLostLocations } = useDataSuite.useDataSuite();
    const { numberFormatter } = formatters();
    const [isWidgetHovered, setIsWidgetHovered] = useState(false);

    const prevFilters = useRef(null);
    const prevDate = useRef(null);

    const handleMouseEnter = () => {
        setIsWidgetHovered(true);
    };
    const handleMouseLeave = () => {
        setIsWidgetHovered(false);
    };

    const onClickSeeDetails = () => {
        navigate({pathname: '/salesstream/details', search: "?Lost%20Locations"});
    };
    
    useEffect(() => {
        if (isEqual(selectedFilters, prevFilters.current) && isEqual(dateString, prevDate.current)) {
            getLostLocations(true);
        } else {
            getLostLocations(false);   
        }
        prevFilters.current = selectedFilters;
        prevDate.current = dateString;

    // eslint-disable-next-line
    }, [selectedFilters, selectedDetailsFilters, dateString, detailsDateString]);

    return (
        <Col onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} style={{ position: 'relative'}} >
            <Row className='d-flex align-items-center'>
                <Col className='w-fc pr-0'>
                    <div className='d-flex align-items-center px-0'>
                    <Title title={widgetTitle}/>
                    <Tooltip title='Displays locations/stores who stopped purchasing year to date who purchased from you in the previous calendar year.' arrow>
                        <div className='pt-3 pb-2'>
                        <IoMdInformationCircle className="text-size-18 " style={{color: Theme().colors.accent}}/>
                        </div>
                    </Tooltip>
                    </div>
                </Col>
                {(widgetOptionsOpen || isWidgetHovered) && ( <>
                    <Col className='w-fc px-1 pt-3 pb-2'>
                        <Button 
                            block 
                            className='py-1 button-label replaceWidgetButton' 
                            size='sm' 
                            color='blueFilter'
                            onClick={() => onClickWidgetDrawerOpen("Replace Widget", true, i)}>
                            <BsPencilSquare color='white' /> 
                        </Button>
                    </Col>
                    <Col className='w-fc px-1 pt-3 pb-2'>
                        <Button
                            block 
                            className='py-1 button-label deleteWidgetButton' 
                            size='sm' 
                            color='redLight'
                            onClick={() => onClickDeleteWidget(w)}>
                            <FaTrash color='white' />
                        </Button>
                    </Col>
                </>
                )}
                <Col className={`w-fc me-3 ms-auto px-1 pt-3 pb-2`}>
                    <Button
                        block
                        className={`py-1 button-label expandWidgetButton`}
                        size='sm'
                        outline={true}
                        color={Theme().colors.accentName}
                        onClick={() => onClickSeeDetails()}>
                        <FaClipboardList color={Theme().colors.accentName} style={{position: 'relative', bottom: '1px'}}/> See Details
                    </Button>
                </Col>
            </Row>
            {(lostLocationsTableData?.length <= 0) &&
            <Row className="mx-auto"style={{height: '70%'}}>
                <Col className="mx-auto my-auto" style={{width:"2.5%"}}>
                    {isLoading &&
                    <Spinner style={{marginLeft:'50%'}} color='beplBlue' animation="border" />
                    }
                    {(!isLoading && (lostLocationsTableData.length <= 0) && filtersEnabled) &&
                    <EmptySearchResultsMessage />
                    }
                    {(!isLoading && (lostLocationsTableData.length <= 0) && !filtersEnabled) &&
                    <EmptyDataMessage />
                    }
                </Col>
            </Row>
            }
            {(lostLocationsTableData?.length > 0) &&
            <Row className="w-100 mb-3 mx-0" style={{minHeight: '315px'}}>
                <Col className="col-12 text-size-14 p-0">
                    <Row style={{overflowY: 'auto', height: '100%'}}>
                        <AutoSizer>
                            {({width, height}) => (
                                <Table className="table lostLocationsTable border-grayLight" id="lostLocationsTable"
                                    width={width * 0.97}
                                    height={height}
                                    headerHeight={34.5}
                                    rowHeight={38}
                                    rowCount={sortedTableData.length}
                                    rowGetter={({ index }) => sortedTableData[index]}
                                >
                                    <Column
                                    label='Location'
                                    dataKey='location_name'
                                    width={width * 0.8}
                                    />
                                    <Column
                                    width={width * 0.2}
                                    label='Total Cases'
                                    dataKey='total_cases'
                                    cellRenderer={({cellData}) => numberFormatter.format(cellData)}
                                    />
                                </Table>
                            )}
                        </AutoSizer>
                    </Row>
                </Col>
            </Row>
            }
        </Col>
    );
    
}

export default LostLocationsWidget;